import { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Select } from '@factorialco/f0-react'
import { Input, Tooltip } from '@factorialco/f0-react/dist/experimental'
import { InfoCircleLine } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { ApproverSelectionContainer } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/ApproverSelectionContainer'
import { type ManualApproveHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/manualApproveHandler'
import { StrategyItem } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/interfaces'
import {
  strategiesFromPayload,
  strategiesToPayload,
} from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/strategyUtils'
import { HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import {
  DEFAULT_DURATION_UNIT,
  getDurationUnitOptions,
  getTranslatedDurationUnit,
  isValidDateUnit,
} from 'modules/workflows/lib/durationUnit'

export const RequestReviewHandlerForm = ({}: HandlerFormComponent) => {
  const { control, watch, setValue, getValues } = useFormContext<ManualApproveHandlerSchema>()

  const payload = watch('nodeData.payload')
  const dueOnValue = payload?.due_on_value
  const dueOnUnit = payload?.due_on_unit ?? DEFAULT_DURATION_UNIT

  const { strategies, fallbackStrategies } = useMemo(() => {
    if (!payload) {
      return {
        strategies: [],
        fallbackStrategies: [],
      }
    }
    return strategiesFromPayload<'strategies' | 'fallbackStrategies'>([
      {
        type: 'ids',
        data: payload.reviewer_ids ?? [],
        groupAs: 'strategies',
      },
      {
        type: 'ids',
        data: payload.fallback_reviewer_ids ?? [],
        groupAs: 'fallbackStrategies',
      },
      {
        type: 'segments',
        data: payload.employee_segments ?? [],
        groupAs: 'strategies',
      },
      {
        type: 'segments',
        data: payload.fallback_employee_segments ?? [],
        groupAs: 'fallbackStrategies',
      },
    ])
  }, [payload])

  const handleMutateStrategies = useCallback(
    (data: { add?: StrategyItem[]; addFallback?: StrategyItem[]; remove?: string[] }) => {
      if (!data.add?.length && !data.remove?.length && !data.addFallback?.length) {
        return
      }

      const { add = [], addFallback = [], remove = [] } = data

      const allStrategies = [...strategies, ...add].filter(
        (strategy) => !remove.includes(strategy.id)
      )
      const allFallbackStrategies = [...fallbackStrategies, ...addFallback].filter(
        (strategy) => !remove.includes(strategy.id)
      )

      const newPayload = strategiesToPayload<typeof payload>(
        [
          { field: 'reviewer_ids', type: 'ids', data: allStrategies },
          { field: 'employee_segments', type: 'segments', data: allStrategies },
          { field: 'fallback_reviewer_ids', type: 'ids', data: allFallbackStrategies },
          { field: 'fallback_employee_segments', type: 'segments', data: allFallbackStrategies },
        ],
        getValues().nodeData.payload
      )

      setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
    },
    [strategies, fallbackStrategies, getValues, setValue]
  )

  const unselectableEmployees = useMemo(
    () => [payload?.reviewer_ids, payload?.fallback_reviewer_ids].flat().filter(Boolean),
    [payload?.reviewer_ids, payload?.fallback_reviewer_ids]
  )

  const unselectableTeams = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && ['anyone_in_team', 'team_leads'].includes(segment.name))
        .map(
          (segment) => segment && ('team_id' in segment.input ? segment.input.team_id : undefined)
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableRoles = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && segment.name === 'job_catalog_role')
        .map((segment) =>
          segment && 'role_id' in segment.input ? segment.input.role_id : undefined
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableStrategies = useMemo(
    () =>
      [strategies, fallbackStrategies]
        .flat()
        .filter((strategy) => ['employee_manager', 'admins'].includes(strategy.type))
        .map((strategy) => strategy.type)
        .concat('resource_owner'),
    [strategies, fallbackStrategies]
  )

  return (
    <Box gap="s16">
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.approval.fields.due_on')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="flexEnd">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label="Duration value"
                  hideLabel
                  placeholder="2"
                  value={String(field.value ?? '')}
                  onChange={(value) => field.onChange(Number(value))}
                  type="number"
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label="Duration unit"
                  hideLabel
                  value={field.value ?? DEFAULT_DURATION_UNIT}
                  onChange={field.onChange}
                  options={getDurationUnitOptions()}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Box>
        <Text size="100" color="textSecondary">
          {dueOnValue && dueOnUnit && isValidDateUnit(dueOnUnit)
            ? i18n.t('workflows.sidepanel.approval.due_date_explanation.with_values', {
                value: dueOnValue,
                unit: getTranslatedDurationUnit(dueOnUnit, dueOnValue),
              })
            : i18n.t('workflows.sidepanel.approval.due_date_explanation.default')}
        </Text>
      </Box>

      <Text color="textPrimary" size="300" weight="medium">
        {i18n.t('workflows.sidepanel.approval.approvers.title')}
      </Text>

      <ApproverSelectionContainer
        entityLabel={i18n.t('workflows.sidepanel.approval.entities.approver')}
        newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_approver')}
        placeholder={i18n.t('workflows.sidepanel.approval.fields.new_assignee_placeholder')}
        onAddStrategies={(data) => handleMutateStrategies({ add: data })}
        onDeleteStrategy={(id) => handleMutateStrategies({ remove: [id] })}
        strategies={strategies}
        unselectableEmployees={unselectableEmployees}
        unselectableRoles={unselectableRoles}
        unselectableStrategies={unselectableStrategies}
        unselectableTeams={unselectableTeams}
      />

      <Divider />

      <Box flexDirection="column" width="full" paddingTop="s8" gap="s8">
        <Box flexDirection="row" alignItems="center" gap="s4">
          <Text color="textPrimary" size="300" weight="medium">
            {i18n.t('workflows.sidepanel.approval.fields.fallback_approver')}
          </Text>
          <Tooltip label={i18n.t('workflows.sidepanel.approval.fields.fallback_approver_tooltip')}>
            <F0Icon icon={InfoCircleLine} size="md" color="secondary" />
          </Tooltip>
        </Box>

        <ApproverSelectionContainer
          entityLabel={i18n.t('workflows.sidepanel.approval.entities.fallback_approver')}
          newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_fallback_approver')}
          placeholder={i18n.t(
            'workflows.sidepanel.approval.fields.new_fallback_assignee_placeholder'
          )}
          maxStrategies={1}
          onAddStrategies={(data) => handleMutateStrategies({ addFallback: data })}
          onDeleteStrategy={(id) => handleMutateStrategies({ remove: [id] })}
          strategies={fallbackStrategies}
          unselectableEmployees={unselectableEmployees}
          unselectableRoles={unselectableRoles}
          unselectableStrategies={unselectableStrategies}
          unselectableTeams={unselectableTeams}
        />
      </Box>
    </Box>
  )
}
